import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FileButton,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconBan,
  IconCheck,
  IconPlus,
  IconRotateClockwise,
  IconSearch,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkInn, checkKpp, checkOgrn, normalizeRequisite } from '@erp/shared';
import { ApiError } from '../api/client';
import {
  ORGANIZATION_KIND_LABELS,
  TAXATION_SYSTEM_LABELS,
  organizationsApi,
  type Organization,
  type OrganizationInput,
  type OrganizationKind,
  type TaxationSystem,
} from '../api/organizations';
import { BankAccountsEditor } from '../components/BankAccountsEditor';
import { RegistryPage } from '../components/RegistryPage';
import { useAuth } from '../auth/AuthContext';

const KIND_OPTIONS = Object.entries(ORGANIZATION_KIND_LABELS).map(([value, label]) => ({ value, label }));
const TAXATION_OPTIONS = Object.entries(TAXATION_SYSTEM_LABELS).map(([value, label]) => ({ value, label }));

const emptyForm: OrganizationInput = {
  shortName: '',
  fullName: '',
  kind: 'LEGAL_ENTITY',
  taxationSystem: 'OSNO',
  inn: '',
  kpp: '',
  ogrn: '',
  ogrnipDate: '',
  okpo: '',
  addressFull: '',
  actualAddressFull: '',
  phone: '',
  email: '',
  directorPosition: 'Генеральный директор',
  directorName: '',
  accountantName: '',
  accountantIsDirector: false,
  edoParticipantId: '',
  logoDataUrl: null,
};

const MAX_LOGO_BYTES = 1_400_000;

/** Файл логотипа → data URL для отправки строкой (см. схему поля на бэкенде). */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Не удалось прочитать файл'));
    reader.readAsDataURL(file);
  });
}

/** Проверка реквизитов — те же функции, что и на сервере, чтобы не ждать ответа API ради опечатки. */
function validateRequisites(data: OrganizationInput): string[] {
  const errors: string[] = [];

  if (!data.inn) {
    if (data.kind !== 'INDIVIDUAL') errors.push('ИНН обязателен для юридического лица и ИП');
  } else {
    const innCheck = checkInn(normalizeRequisite(data.inn));
    if (!innCheck.valid) errors.push(`ИНН: ${innCheck.error}`);
  }

  if (data.kind === 'LEGAL_ENTITY') {
    if (!data.kpp) errors.push('КПП обязателен для юридического лица');
    else {
      const kppCheck = checkKpp(normalizeRequisite(data.kpp));
      if (!kppCheck.valid) errors.push(`КПП: ${kppCheck.error}`);
    }
  }

  if (data.ogrn) {
    const ogrnCheck = checkOgrn(normalizeRequisite(data.ogrn));
    if (!ogrnCheck.valid) errors.push(`${data.kind === 'ENTREPRENEUR' ? 'ОГРНИП' : 'ОГРН'}: ${ogrnCheck.error}`);
  }

  return errors;
}

export function OrganizationsPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<OrganizationInput>(emptyForm);
  const [formError, setFormError] = useState<string[] | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [editingAccounts, setEditingAccounts] = useState<Organization['bankAccounts']>([]);

  async function handleLogoSelect(file: File | null) {
    setLogoError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoError('Логотип должен быть изображением');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setLogoError('Файл слишком большой (максимум ~1.4 МБ)');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setForm((f) => ({ ...f, logoDataUrl: dataUrl }));
    } catch {
      setLogoError('Не удалось прочитать файл');
    }
  }

  const list = useQuery({
    queryKey: ['organizations', { search: debouncedSearch, includeInactive }],
    queryFn: () => organizationsApi.list({ search: debouncedSearch, includeInactive }),
  });

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setEditingAccounts([]);
    setDrawerOpen(true);
  }

  function openEdit(org: Organization) {
    setEditingId(org.id);
    setForm({
      shortName: org.shortName,
      fullName: org.fullName,
      kind: org.kind,
      taxationSystem: org.taxationSystem,
      inn: org.inn,
      kpp: org.kpp ?? '',
      ogrn: org.ogrn ?? '',
      ogrnipDate: org.ogrnipDate ?? '',
      okpo: org.okpo ?? '',
      addressFull: org.addressFull ?? '',
      actualAddressFull: org.actualAddressFull ?? '',
      phone: org.phone ?? '',
      email: org.email ?? '',
      directorPosition: org.directorPosition ?? '',
      directorName: org.directorName ?? '',
      accountantName: org.accountantName ?? '',
      accountantIsDirector: org.accountantIsDirector,
      edoParticipantId: org.edoParticipantId ?? '',
      logoDataUrl: org.logoDataUrl,
    });
    setFormError(null);
    setEditingAccounts(org.bankAccounts);
    setDrawerOpen(true);
  }

  // Драйвер открыт в режиме создания, но организация уже сохранена — переключаемся
  // в режим редактирования, не закрывая форму, чтобы сразу можно было добавить счёт.
  useEffect(() => {
    if (!drawerOpen) setFormError(null);
  }, [drawerOpen]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: OrganizationInput = {
        ...form,
        inn: form.inn || null,
        kpp: form.kpp || null,
        ogrn: form.ogrn || null,
        ogrnipDate: form.ogrnipDate || null,
        okpo: form.okpo || null,
        addressFull: form.addressFull || null,
        actualAddressFull: form.actualAddressFull || null,
        phone: form.phone || null,
        email: form.email || null,
        directorPosition: form.directorPosition || null,
        directorName: form.directorName || null,
        accountantName: form.accountantName || null,
        edoParticipantId: form.edoParticipantId || null,
      };
      return editingId ? organizationsApi.update(editingId, payload) : organizationsApi.create(payload);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
      notifications.show({ color: 'green', message: 'Организация сохранена' });
      if (!editingId) {
        // Остаёмся в форме, переключившись в режим редактирования — чтобы можно
        // было сразу добавить банковский счёт только что созданной организации.
        setEditingId(data.organization.id);
        setEditingAccounts(data.organization.bankAccounts);
      }
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors = validateRequisites(form);
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const toggleActiveMutation = useMutation({
    mutationFn: (org: Organization) =>
      org.isActive ? organizationsApi.deactivate(org.id) : organizationsApi.activate(org.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const addBankAccount = useMutation({
    mutationFn: (data: Parameters<typeof organizationsApi.addBankAccount>[1]) =>
      organizationsApi.addBankAccount(editingId!, data),
    onSuccess: (data) => {
      setEditingAccounts((prev) => [...prev, data.bankAccount]);
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const setDefaultBankAccount = useMutation({
    mutationFn: (accountId: string) => organizationsApi.updateBankAccount(editingId!, accountId, { isDefault: true }),
    onSuccess: (data) => {
      setEditingAccounts((prev) =>
        prev.map((a) => (a.id === data.bankAccount.id ? data.bankAccount : { ...a, isDefault: false })),
      );
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const deleteBankAccount = useMutation({
    mutationFn: (accountId: string) => organizationsApi.deleteBankAccount(editingId!, accountId),
    onSuccess: (_data, accountId) => {
      setEditingAccounts((prev) => prev.filter((a) => a.id !== accountId));
      void queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Организации"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Создать организацию
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по наименованию или ИНН"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Checkbox
            label="Показывать отключённые"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Наименование</Table.Th>
            <Table.Th>Вид</Table.Th>
            <Table.Th>ИНН</Table.Th>
            <Table.Th>КПП</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((org) => (
            <Table.Tr key={org.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(org)}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {org.shortName}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {ORGANIZATION_KIND_LABELS[org.kind]}
                </Text>
              </Table.Td>
              <Table.Td>{org.inn ?? '—'}</Table.Td>
              <Table.Td>{org.kpp ?? '—'}</Table.Td>
              <Table.Td>
                <Badge color={org.isActive ? 'green' : 'gray'} variant="light">
                  {org.isActive ? 'Активна' : 'Отключена'}
                </Badge>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                  <Tooltip label={org.isActive ? 'Отключить' : 'Включить'}>
                    <ActionIcon
                      variant="subtle"
                      color={org.isActive ? 'red' : 'green'}
                      onClick={() => toggleActiveMutation.mutate(org)}
                    >
                      {org.isActive ? <IconBan size={16} /> : <IconRotateClockwise size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Организации не найдены
        </Text>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Организация' : 'Новая организация'}
        position="right"
        size="lg"
        padding="lg"
      >
        <Stack gap="sm">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              <Stack gap={2}>
                {formError.map((e, i) => (
                  <Text key={i} size="sm">
                    {e}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group grow>
            <TextInput
              label="Краткое наименование"
              value={form.shortName}
              onChange={(e) => setForm({ ...form, shortName: e.currentTarget.value })}
              required
              disabled={!canEdit}
            />
            <Select
              label="Вид"
              data={KIND_OPTIONS}
              value={form.kind}
              onChange={(v) => setForm((f) => ({ ...f, kind: (v as OrganizationKind) ?? f.kind }))}
              disabled={!canEdit}
              allowDeselect={false}
            />
          </Group>

          <TextInput
            label="Полное наименование"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
            required
            disabled={!canEdit}
          />

          <Select
            label="Система налогообложения"
            data={TAXATION_OPTIONS}
            value={form.taxationSystem}
            onChange={(v) => setForm((f) => ({ ...f, taxationSystem: (v as TaxationSystem) ?? f.taxationSystem }))}
            disabled={!canEdit}
            allowDeselect={false}
          />

          <Group grow>
            <TextInput
              label="ИНН"
              value={form.inn ?? ''}
              onChange={(e) => setForm({ ...form, inn: e.currentTarget.value })}
              required={form.kind !== 'INDIVIDUAL'}
              disabled={!canEdit}
            />
            {form.kind === 'LEGAL_ENTITY' && (
              <TextInput
                label="КПП"
                value={form.kpp ?? ''}
                onChange={(e) => setForm({ ...form, kpp: e.currentTarget.value })}
                required
                disabled={!canEdit}
              />
            )}
          </Group>

          <Group grow>
            <TextInput
              label={form.kind === 'ENTREPRENEUR' ? 'ОГРНИП' : 'ОГРН'}
              value={form.ogrn ?? ''}
              onChange={(e) => setForm({ ...form, ogrn: e.currentTarget.value })}
              disabled={!canEdit}
            />
            {form.kind === 'ENTREPRENEUR' && (
              <TextInput
                label="Дата присвоения ОГРНИП"
                type="date"
                value={form.ogrnipDate ?? ''}
                onChange={(e) => setForm({ ...form, ogrnipDate: e.currentTarget.value })}
                disabled={!canEdit}
              />
            )}
          </Group>

          <TextInput
            label="Адрес"
            value={form.addressFull ?? ''}
            onChange={(e) => setForm({ ...form, addressFull: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <Group grow>
            <TextInput
              label="Телефон"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="Email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <Divider label="Подписанты печатной формы" />

          <Group grow>
            {form.kind !== 'INDIVIDUAL' && (
              <TextInput
                label="Должность руководителя"
                value={form.directorPosition ?? ''}
                onChange={(e) => setForm({ ...form, directorPosition: e.currentTarget.value })}
                disabled={!canEdit}
              />
            )}
            <TextInput
              label={form.kind === 'INDIVIDUAL' ? 'ФИО' : 'ФИО руководителя'}
              value={form.directorName ?? ''}
              onChange={(e) => setForm({ ...form, directorName: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <Checkbox
            label="Обязанности главного бухгалтера возложены на руководителя"
            checked={form.accountantIsDirector}
            onChange={(e) => setForm({ ...form, accountantIsDirector: e.currentTarget.checked })}
            disabled={!canEdit}
          />

          {!form.accountantIsDirector && (
            <TextInput
              label="ФИО главного бухгалтера"
              value={form.accountantName ?? ''}
              onChange={(e) => setForm({ ...form, accountantName: e.currentTarget.value })}
              disabled={!canEdit}
            />
          )}

          <TextInput
            label="Идентификатор участника ЭДО"
            value={form.edoParticipantId ?? ''}
            onChange={(e) => setForm({ ...form, edoParticipantId: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <Divider label="Логотип для печатных форм" />

          {logoError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {logoError}
            </Alert>
          )}

          <Group>
            {/* Тёмная подложка: загруженные логотипы часто на сплошном тёмном
                фоне без альфа-канала — на светлом фоне он иначе читался бы квадратом. */}
            <Avatar src={form.logoDataUrl} radius="sm" size={64} style={{ backgroundColor: '#17171a' }}>
              {!form.logoDataUrl && form.shortName.slice(0, 2)}
            </Avatar>
            {canEdit && (
              <Stack gap={6}>
                <FileButton onChange={(f) => void handleLogoSelect(f)} accept="image/png,image/jpeg,image/webp,image/svg+xml">
                  {(props) => (
                    <Button {...props} variant="default" size="xs" leftSection={<IconUpload size={14} />}>
                      Загрузить логотип
                    </Button>
                  )}
                </FileButton>
                {form.logoDataUrl && (
                  <Button
                    variant="subtle"
                    color="red"
                    size="xs"
                    leftSection={<IconTrash size={14} />}
                    onClick={() => setForm((f) => ({ ...f, logoDataUrl: null }))}
                  >
                    Убрать логотип
                  </Button>
                )}
              </Stack>
            )}
          </Group>

          {canEdit && (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={saveMutation.isPending}
              mt="xs"
            >
              Сохранить
            </Button>
          )}

          {editingId && (
            <>
              <Divider label="Банковские счета" mt="md" />
              <BankAccountsEditor
                accounts={editingAccounts}
                canEdit={canEdit}
                onAdd={(data) => addBankAccount.mutateAsync(data).then(() => undefined)}
                onSetDefault={(id) => setDefaultBankAccount.mutateAsync(id).then(() => undefined)}
                onDelete={(id) => deleteBankAccount.mutateAsync(id).then(() => undefined)}
              />
            </>
          )}
        </Stack>
      </Drawer>
    </RegistryPage>
  );
}
